import { Fragment, useEffect, useMemo } from 'react';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	InputAction,
	InputKey,
	VehicleTuningCategory,
	VehicleTuningSubcategory,
} from '~/shared';
import { useTuningCategories } from '../../hooks';
import {
	setActiveIndex,
	setActiveIndexNext,
	setActiveIndexPrev,
	setActiveStage,
	setActiveTuningCategory,
	setActiveTuningGroup,
	setActiveTuningSubcategory,
	setMaxIndex,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	TUNING_GROUP_ICONS,
	TUNING_GROUP_SUBCATEGORIES,
	TUNING_GROUP_CATEGORY,
	TuningGroup,
	VEHICLE_TUNING_CATEGORY_LABELS,
} from '../../tuningGroups.model';
import { Categories } from '../Categories';
import { CATEGORY_ICONS } from '../Main/Main.model';
import { StyledContainer, StyledControlsContainer } from '../Main/Main.styled';
import { MenuItem } from '../MenuItem';

const GROUP_ORDER: TuningGroup[] = [
	TuningGroup.FRONT_BUMPER,
	TuningGroup.REAR_BUMPER,
	TuningGroup.DOORS,
	TuningGroup.BONNET,
	TuningGroup.BOOT,
	TuningGroup.SKIRTS,
	TuningGroup.ROOF,
	TuningGroup.FENDERS,
	TuningGroup.LIGHTS,
	TuningGroup.EXHAUST,
	TuningGroup.INTERIOR,
	TuningGroup.CHASSIS,
	TuningGroup.ENGINE,
];

interface IExteriorMenuKitsEntry {
	type: 'kits';
	id: string;
	name: string;
	icon: string;
	count: number;
}

interface IExteriorMenuGroupEntry {
	type: 'group';
	id: TuningGroup;
	name: string;
	icon: string;
	count: number;
	subcategoriesWithItems: VehicleTuningSubcategory[];
}

type ExteriorMenuEntry = IExteriorMenuKitsEntry | IExteriorMenuGroupEntry;

const ExteriorTuning = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const { shopCategories, shopItems } = useTuningCategories();
	const modalState = useObservable(rage.modal.getCurrentModal());
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);

	const itemsBySubcategory = useMemo(() => {
		const map = new Map<
			VehicleTuningSubcategory,
			{ total: number; nonStock: number }
		>();
		shopItems.forEach((item) => {
			if (!item.subcategoryId) {
				return;
			}
			const entry = map.get(item.subcategoryId) ?? { total: 0, nonStock: 0 };
			entry.total += 1;
			if (!item.id.startsWith('stock_')) {
				entry.nonStock += 1;
			}
			map.set(item.subcategoryId, entry);
		});
		return map;
	}, [shopItems]);

	const entries = useMemo<ExteriorMenuEntry[]>(() => {
		const result: ExteriorMenuEntry[] = [];

		const kitsCategory = shopCategories.find(
			(c) => c.id === VehicleTuningCategory.KITS,
		);
		const kitsCount = shopItems.filter(
			(i) => i.categoryId === VehicleTuningCategory.KITS,
		).length;
		if (kitsCategory && kitsCount > 0) {
			result.push({
				type: 'kits',
				id: kitsCategory.id,
				name: kitsCategory.name,
				icon: CATEGORY_ICONS[kitsCategory.id],
				count: kitsCount,
			});
		}

		GROUP_ORDER.forEach((group) => {
			const cats = TUNING_GROUP_SUBCATEGORIES[group];
			let count = 0;
			const subcategoriesWithItems: VehicleTuningSubcategory[] = [];
			cats.forEach((id) => {
				const counts = itemsBySubcategory.get(id) ?? {
					total: 0,
					nonStock: 0,
				};
				if (counts.nonStock > 0) {
					count += counts.total;
					subcategoriesWithItems.push(id);
				}
			});
			if (count === 0) {
				return;
			}
			result.push({
				type: 'group',
				id: group,
				name: VEHICLE_TUNING_CATEGORY_LABELS[TUNING_GROUP_CATEGORY[group]],
				icon: TUNING_GROUP_ICONS[group],
				count,
				subcategoriesWithItems,
			});
		});

		return result;
	}, [shopCategories, shopItems, itemsBySubcategory]);

	const handleBack = () => {
		rage.vehicleTuningShop
			.removeTemporaryItems()
			.catch((err) => rage.notifications.showException(err));
		rage.vehicleTuningShop
			.resetWheelsSettings()
			.catch((err) => rage.notifications.showException(err));

		dispatch(setActiveStage(TuningStudioStage.MAIN));
		dispatch(setActiveIndex(2));
	};

	const handleSelect = (index: number) => {
		const entry = entries[index];
		if (!entry) {
			return;
		}

		if (entry.type === 'kits') {
			dispatch(setActiveIndex(0));
			dispatch(setActiveTuningCategory(VehicleTuningCategory.KITS));
			dispatch(setActiveTuningSubcategory(null));
			dispatch(setActiveTuningGroup(null));
			dispatch(setActiveStage(TuningStudioStage.TUNING_MENU));
			return;
		}

		dispatch(setActiveTuningGroup(entry.id));

		const designCatsCount = TUNING_GROUP_SUBCATEGORIES[entry.id].length;
		if (designCatsCount === 1 && entry.subcategoriesWithItems.length === 1) {
			dispatch(setActiveTuningCategory(null));
			dispatch(setActiveTuningSubcategory(entry.subcategoriesWithItems[0]));
			dispatch(setActiveIndex(0));
			dispatch(setActiveStage(TuningStudioStage.TUNING_MENU));
			return;
		}

		dispatch(setActiveIndex(0));
		dispatch(setActiveStage(TuningStudioStage.SUBCATEGORY_MENU));
	};

	const leftControls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
			},
			{
				text: 'Выбрать категорию',
				keys: [InputKey.Enter],
				onClick: () => handleSelect(activeIndex),
			},
			{
				text: 'Крутить камеру',
				keys: [InputKey.MouseRight],
			},
		],
		[activeIndex, entries],
	);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleTuningShop.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		handleSelect(activeIndex);
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		handleBack();
	});

	useEffect(() => {
		const maxIndex = Math.max(0, entries.length - 1);
		dispatch(setMaxIndex(maxIndex));
	}, [entries, dispatch]);

	return (
		<Fragment>
			<StyledContainer>
				<Categories
					name="Внешний тюнинг"
					activeIndex={activeIndex}
					totalCount={entries.length}
					handleBack={handleBack}
					handlePrev={() => dispatch(setActiveIndexPrev())}
					handleNext={() => dispatch(setActiveIndexNext())}
				>
					{entries.map((entry, index) => (
						<MenuItem
							type="menu"
							name={entry.name}
							icon={entry.icon}
							count={entry.count}
							key={entry.type === 'kits' ? `kits:${entry.id}` : entry.id}
							onClick={() => handleSelect(index)}
							active={activeIndex === index}
							id={`exteriorMenuItem#${index}`}
						/>
					))}
				</Categories>
			</StyledContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { ExteriorTuning };
